import * as styles from "./CoreProductInfoContainer.css";
import {
  TopBlock,
  Description,
  ProductAttributes,
  FindOutMore
} from "@growth/page.products";
import { InfoBlock } from "@shared/ui.components";
import { PricingOptions } from "../PricingOptions/PricingOptions";
import { Typography } from "@shared/ui.primitives";
import type {
  RelatedVariant,
  Maybe,
  CoreProductInfo,
  ProductPricingDetailsType,
  PricingOption
} from "@shared/graphql.types";
import { useStickyProductInfo } from "./hooks/useStickyProductInfo";
import { useSidebarTopOffset } from "./hooks/useSidebarTopOffset";
import { useHasMounted } from "utils/wrappers";
import { useScreenViewport } from "lib/checkViewport";

export interface CoreProductInfoContainerProps {
  coreProductInfo: CoreProductInfo;
  filteredAttributes: string[];
  trackReviewsClick: () => void;
  onATB: ({
    membershipSKU,
    locationTypeDetails
  }: {
    membershipSKU: string;
    locationTypeDetails: string;
  }) => Promise<void>;
  handleWishlist: () => void;
  isInWishlist: boolean;
  relatedVariants?: Maybe<RelatedVariant[]>;
  selectedVariantSlug?: string;
  handleSubmit: ({ email }: { email: string }) => Promise<boolean>;
  submitted: boolean;
  membershipInBag: boolean;
  isMember: boolean;
  isFreeTrialVariant: boolean;
  promoBanner?: {
    title: string;
    color: string;
  };
  isStrikethroughVariant?: boolean;
  promoPrice?: ProductPricingDetailsType | null;
  price?: ProductPricingDetailsType | null;
  onPriceChange?: ((price: PricingOption) => void) | null;
}

export const CoreProductInfoContainer = ({
  coreProductInfo,
  filteredAttributes,
  onATB,
  trackReviewsClick,
  handleWishlist,
  isInWishlist,
  relatedVariants,
  selectedVariantSlug,
  handleSubmit,
  submitted,
  membershipInBag,
  isMember,
  isFreeTrialVariant,
  promoBanner,
  isStrikethroughVariant = false,
  promoPrice = null,
  price = null,
  onPriceChange = null
}: CoreProductInfoContainerProps) => {
  const {
    infoBanner,
    brandName,
    title,
    subtitle,
    review,
    ribbons,
    description,
    isOutOfStock,
    membershipOptions,
    pricingOptions,
    sku,
    prices
  } = coreProductInfo;
  // We conditionally pass this ref to either OutOfStock or PricingOptions
  const { ref, isScrolling, useSticky, showDescriptionAndAttributes } =
    useStickyProductInfo();

  const hasMounted = useHasMounted();
  const { isMobile } = useScreenViewport();
  const { sidebarOffsetTop } = useSidebarTopOffset(hasMounted, isMobile);

  // Determine container class based on sticky behavior
  const containerClassName = useSticky
    ? styles.coreProductInfoContainer
    : styles.nonStickyContainer;

  return (
    <div
      id="core-product-info-container"
      className={containerClassName}
      style={{
        top: `${sidebarOffsetTop}px`
      }}
    >
      <div
        className={`${
          isScrolling && useSticky ? styles.scrollingInnerContainer : ""
        } ${styles.innerContainer}`}
      >
        {showDescriptionAndAttributes && infoBanner && (
          <InfoBlock>
            <Typography color="primary-dark" variant="body-md">
              {infoBanner}
            </Typography>
          </InfoBlock>
        )}
        <TopBlock
          brandName={brandName ?? undefined}
          title={title}
          subtitle={subtitle || ""}
          rating={review.averageRating}
          reviewCount={review.count}
          ribbons={ribbons ?? undefined}
          trackReviewsClick={trackReviewsClick}
          promoBanner={promoBanner}
          isStrikethroughVariant={isStrikethroughVariant}
          price={price}
          promoPrice={promoPrice}
        />
        {showDescriptionAndAttributes && (
          <>
            <Description text={description ?? ""} />
            <ProductAttributes attributes={filteredAttributes} />
          </>
        )}
        <PricingOptions
          membershipOptions={membershipOptions}
          pricingOptions={pricingOptions}
          variant={{ sku, prices }}
          onAddToBag={onATB}
          handleWishlist={handleWishlist}
          isInWishlist={isInWishlist}
          ref={ref}
          context="desktop"
          relatedVariants={relatedVariants}
          selectedVariantSlug={selectedVariantSlug}
          isOutOfStock={isOutOfStock}
          handleSubmit={handleSubmit}
          submitted={submitted}
          membershipInBag={membershipInBag}
          isMember={isMember}
          isFreeTrialVariant={isFreeTrialVariant}
          onPriceChange={onPriceChange}
          isStrikethroughVariant={isStrikethroughVariant}
        />

        {!isScrolling && <FindOutMore id="find-out-more-anchor" />}
      </div>
    </div>
  );
};
